
import { v4 as uuidv4 } from 'uuid';

export interface NotificationPreference {
  id: string;
  userId: string;
  type: string;
  channel: 'email' | 'sms' | 'push';
  enabled: boolean;
}

export interface NotificationTemplate {
  id: string;
  name: string;
  type: string;
  subject?: string;
  body: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

// Mocked data and functions for notification preferences
const mockNotificationPreferences: NotificationPreference[] = [
  { id: '1', userId: 'user1', type: 'referral_received', channel: 'email', enabled: true },
  { id: '2', userId: 'user1', type: 'points_awarded', channel: 'email', enabled: true },
  { id: '3', userId: 'user1', type: 'referral_signup', channel: 'sms', enabled: false },
];

const mockNotificationTemplates: NotificationTemplate[] = [
  {
    id: '1',
    name: 'Referral Received',
    type: 'referral_received',
    subject: 'New Referral Received',
    body: 'Hello {{name}}, you have received a new referral from {{referrerName}}.',
    variables: ['name', 'referrerName'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Points Awarded',
    type: 'points_awarded',
    subject: 'You have been awarded points!',
    body: 'Hello {{name}}, you have been awarded {{points}} points.',
    variables: ['name', 'points'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

export const getUserNotificationPreference = async (userId: string): Promise<NotificationPreference[]> => {
  // Mock API call
  await new Promise(resolve => setTimeout(resolve, 300));
  return mockNotificationPreferences.filter(pref => pref.userId === userId);
};

export const updateUserNotificationPreferences = async (
  userId: string, 
  preferences: Partial<NotificationPreference>[]
): Promise<NotificationPreference[]> => {
  // Mock API call
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // In a real app, you would update the preferences in the database
  return preferences.map(pref => ({
    id: pref.id || uuidv4(),
    userId,
    type: pref.type || 'unknown',
    channel: pref.channel || 'email',
    enabled: pref.enabled === undefined ? true : pref.enabled
  }));
};

export const getNotificationTemplates = async (): Promise<NotificationTemplate[]> => {
  // Mock API call
  await new Promise(resolve => setTimeout(resolve, 300));
  return mockNotificationTemplates;
};

export const updateNotificationTemplate = async (
  templateId: string,
  data: Partial<NotificationTemplate>
): Promise<NotificationTemplate> => {
  // Mock API call
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const template = mockNotificationTemplates.find(t => t.id === templateId);
  
  if (!template) {
    throw new Error('Template not found');
  }
  
  // In a real app, you would update the template in the database
  return {
    ...template,
    ...data,
    updatedAt: new Date().toISOString()
  };
};

export const getUserNotificationPreferences = getUserNotificationPreference;

// Add missing exports that are referenced in utils/referral/index.ts
export const sendEmailNotification = async (
  to: string,
  subject: string,
  body: string,
  variables?: Record<string, string>
): Promise<boolean> => {
  console.log(`Mock sending email to: ${to}, subject: ${subject}`);
  return true;
};

export const sendReferralNotification = async (
  userId: string,
  notificationType: string,
  data?: any
): Promise<boolean> => {
  console.log(`Mock sending referral notification to user: ${userId}, type: ${notificationType}`);
  return true;
};

export const notifyReferralStatus = async (
  referralId: string,
  status: string
): Promise<boolean> => {
  console.log(`Mock notifying about referral ID: ${referralId}, status: ${status}`);
  return true;
};

export const setUserNotificationPreference = async (
  userId: string,
  preferences: Partial<NotificationPreference>
): Promise<NotificationPreference> => {
  console.log(`Mock setting notification preferences for user: ${userId}`);
  return {
    id: uuidv4(),
    userId,
    type: preferences.type || 'unknown',
    channel: preferences.channel || 'email',
    enabled: preferences.enabled || false
  };
};

export const getNotificationPreferences = async (): Promise<NotificationPreference[]> => {
  return mockNotificationPreferences;
};
